import type { ReactNode } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type ConfirmPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** 复制到剪贴板的纯文本；缺省则隐藏复制按钮 */
  copyText?: string | null;
  /** 标题旁额外操作（如「写入后 / 对照」切换） */
  toolbar?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
};

/**
 * Hermes / 未来官网等确认流共用的预览弹窗壳：标题 + 复制 + 可滚动 body。
 * body 由调用方按 artifact kind 定制（Markdown、字段、代码等）。
 */
export function ConfirmPreviewDialog({
  open,
  onOpenChange,
  title,
  description,
  copyText,
  toolbar,
  children,
  className,
  contentClassName,
}: ConfirmPreviewDialogProps) {
  async function handleCopy() {
    const text = copyText?.trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("已复制");
    } catch {
      toast.error("复制失败");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[min(85vh,720px)] w-full flex-col gap-4 sm:max-w-2xl",
          className,
        )}
        showCloseButton
      >
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {toolbar}
            {copyText?.trim() ? (
              <Button type="button" size="sm" variant="outline" onClick={() => void handleCopy()}>
                <Copy className="size-3.5" />
                复制
              </Button>
            ) : null}
          </div>
        </DialogHeader>
        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto rounded-xl border border-border/60 bg-muted/20 p-3",
            contentClassName,
          )}
        >
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
